<template>
  <div>
    <!-- Mobile Number Header -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <span class="text-muted font-small-3">
        Mobile: <strong>{{ mobileNumber }}</strong>
      </span>
    </div>

    <!-- Date Filter Row -->
    <b-row class="mb-1">
      <b-col cols="12" sm="2">
        <b-form-group label="From Date" label-class="font-small-4" class="mb-0">
          <flat-pickr
            v-model="filter.fromDate"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="From Date"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12" sm="2">
        <b-form-group label="To Date" label-class="font-small-4" class="mb-0">
          <flat-pickr
            v-model="filter.toDate"
            :config="dateConfig"
            class="form-control form-control-sm"
            placeholder="To Date"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        sm="4"
        class="d-flex align-items-end justify-content-start mt-1 mt-sm-0"
      >
        <b-button
          variant="primary"
          size="sm"
          class="mr-1"
          :disabled="loading"
          @click="applyFilter"
        >
          <feather-icon icon="SearchIcon" size="14" class="mr-25" />
          Filter
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="loading"
          @click="clearFilter"
        >
          <feather-icon icon="XIcon" size="14" class="mr-25" />
          Clear
        </b-button>
      </b-col>
    </b-row>

    <div class="d-flex align-items-center justify-content-between mb-1">
        <!-- Left side -->
        <div class="d-flex align-items-center">
            <label class="mr-2 mb-0 font-small-3">Per Page</label>

            <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            size="sm"
            class="w-auto"
            @change="onPerPageChange"
            />

            <label class="mb-0 font-small-3 ml-1">
            Total {{ totalRecords }} entries
            </label>
        </div>

        <!-- Right side (Excel Button) -->
        <div v-if="callHistory.length">
            <b-button
            variant="success"
            size="sm"
            @click="downloadCSV"
            >
            <feather-icon icon="DownloadIcon" size="14" class="mr-25" />
            Export Excel
            </b-button>
        </div>
    </div>

    <!-- Table -->
    <b-table
      responsive
      :items="paginatedCallHistory"
      :fields="tableColumns"
      show-empty
      empty-text="No Call History found"
      :busy="loading"
      small
    >
      <template #table-busy>
        <div class="text-center text-primary my-2">
          <b-spinner class="align-middle mr-1" small />
          <strong>Loading...</strong>
        </div>
      </template>

      <!-- SL -->
      <template #cell(sl)="data">
        {{ (currentPage - 1) * perPage + data.index + 1 }}
      </template>

          <!-- Call ID -->
      <template #cell(call_id)="data">
        <span class="font-weight-bold text-primary">
          {{ data.item.callID }}
        </span>
      </template>

      <!-- Duration -->
      <template #cell(duration)="data">
        {{ data.item.duration }}
      </template>

      <!-- Cost -->
      <template #cell(call_cost)="data">
        <b-badge variant="light-info">
          {{ data.item.call_cost }}
        </b-badge>
      </template>

      <!-- Date & Time -->
      <template #cell(connectTimestamp)="data">
        {{ data.item.connectTimestamp || "" }}
      </template>
      <!-- SI -->
      <template #cell(si)="data">
        {{ data.item.si }}
      </template>

      <!-- Caller ID -->
      <template #cell(callerID)="data">
        {{ data.item.callerID }}
      </template>

      <!-- Region -->
      <template #cell(region)="data">
        {{ data.item.region }}
      </template>

      <!-- Dialed No -->
      <template #cell(dialed_no)="data">
        {{ data.item.dialed_no}}
      </template>

      <!-- CDR ID -->
      <template #cell(cdr_id)="data">
        {{ data.item.cdrID }}
      </template>
      <!-- CDR ID -->
      <template #cell(call_type)="data">
        {{ data.item.callType}}
      </template>
    </b-table>

    <!-- Pagination -->
    <b-row class="mt-1">
      <b-col
        cols="12"
        sm="5"
        class="d-flex align-items-center justify-content-center justify-content-sm-start"
      >
        <span class="text-muted font-small-3">
          Showing {{ fromEntry }} to {{ toEntry }} of {{ totalRecords }} entries
        </span>
      </b-col>
      <b-col
        cols="12"
        sm="7"
        class="d-flex align-items-center justify-content-center justify-content-sm-end"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRecords"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0 mt-1 mt-sm-0"
          prev-class="prev-item"
          next-class="next-item"
          size="sm"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="16" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="16" />
          </template>
        </b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BTable,
  BBadge,
  BPagination,
  BRow,
  BCol,
  BFormSelect,
  BFormGroup,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import api from "@/services/api";

export default {
  name: "KothonTransaction",

  components: {
    BTable,
    BBadge,
    BPagination,
    BRow,
    BCol,
    BFormSelect,
    BFormGroup,
    BButton,
    BSpinner,
    flatPickr,
  },

  props: {
    mobileNumber: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      callHistory: [],
      currentPage: 1,
      perPage: 10,
      perPageOptions: [10, 20, 50],

      // UI-bound filter fields (only committed to API on Filter click)
      filter: {
        fromDate: null,
        toDate: null,
      },

      // Committed filter — what's actually sent to the API
      activeFilter: {
        fromDate: null,
        toDate: null,
      },

      dateConfig: {
        altFormat: "d/m/Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },

     tableColumns: [
        { key: "sl",               label: "#",            sortable: false },
        { key: "call_id",          label: "Call ID",         sortable: false },
        { key: "duration",         label: "Duration", sortable: false },
        { key: "call_cost",        label: "Call Cost",         sortable: false },
        { key: "connectTimestamp", label: "Date & Time",  sortable: false },
        { key: "si",               label: "SI",  sortable: false },
        { key: "callerID",         label: "Caller ID",    sortable: false },
        { key: "region",           label: "Region",    sortable: false },
        { key: "dialed_no",        label: "Dialed No",    sortable: false },
        { key: "cdr_id",           label: "CdrID",    sortable: false },
        { key: "call_type",        label: "Call Type",    sortable: false },
      ]
    };
  },

  computed: {
    totalRecords() {
      return this.callHistory.length;
    },

    paginatedCallHistory() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.callHistory.slice(start, start + this.perPage);
    },

    fromEntry() {
      if (!this.totalRecords) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },

    toEntry() {
      return Math.min(this.currentPage * this.perPage, this.totalRecords);
    },
  },

  watch: {
    // Re-fetch when parent opens modal with a different mobile number
    mobileNumber(newVal) {
      if (newVal) {
        this.resetState();
        this.fetchCallHistory();
      }
    },
  },

  mounted() {
    if (this.mobileNumber) {
    //   this.fetchCallHistory();
    }
  },

  methods: {
   downloadCSV() {
      if (!this.callHistory.length) return;

      // Header row — matches tableColumns exactly
      const headers = [
        "SL",
        "Call ID",
        "Duration",
        "Call Cost",
        "Date & Time",
        "SI",
        "Caller ID",
        "Region",
        "Dialed No",
        "CDR ID",
        "Call Type",
      ];

      // Data rows — mapped from actual API field names
      const rows = this.callHistory.map((item, index) => [
        index + 1,
        item.callID,
        item.duration,
        item.call_cost,
        item.connectTimestamp,
        item.si,
        item.callerID ,
        item.region,
        item.dialed_no,
        item.cdrID,
        item.callType,
      ]);

      // Combine header + rows → CSV string
      const csvContent = [headers, ...rows]
        .map(row =>
          row
            .map(value => `"${String(value).replace(/"/g, '""')}"`)
            .join(",")
        )
        .join("\n");

      // Trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url  = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `CallHistory_${this.mobileNumber}_${Date.now()}.csv`
      );

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // ✅ Filter button: validate → commit → API call
    applyFilter() {
      if (!this.filter.fromDate || !this.filter.toDate) {
        this.$bvToast.toast("Please select both From Date and To Date.", {
          title: "Validation",
          variant: "warning",
          solid: true,
        });
        return;
      }

      // Commit UI filter to active filter
      this.activeFilter.fromDate = this.filter.fromDate;
      this.activeFilter.toDate   = this.filter.toDate;
      this.currentPage           = 1;
      this.fetchCallHistory();
    },

    // ✅ Clear button: wipe all filter state → re-fetch without filters
    clearFilter() {
      this.filter.fromDate       = null;
      this.filter.toDate         = null;
      this.activeFilter.fromDate = null;
      this.activeFilter.toDate   = null;
      this.currentPage           = 1;
      this.callHistory            = [];
    //   this.fetchCallHistory();
    },

    // ✅ Per-page dropdown change → reset page → re-fetch (triggers new slice)
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchCallHistory();
    },

    // ✅ Core API call — always uses activeFilter (not UI filter)
    async fetchCallHistory() {
      this.loading      = true;
      this.callHistory = [];

      try {
        const params = {
          mobile_number: this.mobileNumber,
        };

        // Only include date params if filter is active
        if (this.activeFilter.fromDate) params.from_date = this.activeFilter.fromDate;
        if (this.activeFilter.toDate)   params.to_date   = this.activeFilter.toDate;

        const res = await api.get("/kothon/api/v1/kothon_call_history", { params });

        this.callHistory = Array.isArray(res.data) ? res.data : [];
      } catch (error) {
        console.error("Transaction fetch failed:", error);

        this.$bvToast.toast(
          error.response?.data?.message || "Failed to load call history",
          {
            title: "Error",
            variant: "danger",
            solid: true,
          }
        );

        this.callHistory = [];
      } finally {
        this.loading = false;
      }
    },

    // ✅ Full state reset when mobile number changes via parent
    resetState() {
      this.callHistory          = [];
      this.currentPage           = 1;
      this.filter.fromDate       = null;
      this.filter.toDate         = null;
      this.activeFilter.fromDate = null;
      this.activeFilter.toDate   = null;
    },

    formatDateTime(ms) {
      if (!ms) return "";
      const d   = new Date(ms);
      const pad = (n) => String(n).padStart(2, "0");
      return (
        `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
        `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
      );
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>

<style scoped>
.text-truncate {
  vertical-align: middle;
}
</style>